import { useState } from 'react'
import {
  Box, Button, Typography, IconButton, Tooltip, Stack, Dialog, DialogTitle,
  DialogContent, DialogActions, Table, TableBody, TableCell, TableRow,
  Chip, Divider,
} from '@mui/material'
import {
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  OpenInNew as OpenLeadIcon,
} from '@mui/icons-material'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router'
import { DataTable } from '../../components/ui/DataTable'
import { LoadingSpinner } from '../../components/shared/LoadingSpinner'
import { themeColors } from '../../app/layout/theme'
import { leadFormsApi, FormSubmissionResponse } from '../../lib/api/leadFormsApi'
import { maskPhoneNumber } from '../../utils/phoneUtils'

interface LeadFormResponsesProps {
  formId?:   string   // override useParams when embedded inside builder
  embedded?: boolean  // hides back/nav header when true
}

export const LeadFormResponses = ({ formId: propFormId, embedded = false }: LeadFormResponsesProps = {}) => {
  const params   = useParams<{ id: string }>()
  const id       = propFormId ?? params.id
  const navigate = useNavigate()
  const [page, setPage]         = useState(1)
  const [pageSize]              = useState(20)
  const [selectedResponse, setSelectedResponse] = useState<FormSubmissionResponse | null>(null)

  const { data: formData } = useQuery({
    queryKey: ['leadForm', id],
    queryFn:  () => leadFormsApi.getById(id!),
    enabled:  !!id,
    staleTime: 60_000,
  })

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['leadFormResponses', id, page],
    queryFn:  () => leadFormsApi.getResponses(id!, { page, pageSize }),
    enabled:  !!id,
    staleTime: 30_000,
  })

  const responses   = data?.items ?? []
  const total       = data?.total ?? 0
  const totalPages  = data?.totalPages ?? 1

  const columns = [
    {
      id: 'id',
      label: 'Submission ID',
      render: (_: any, row: FormSubmissionResponse) => (
        <Typography sx={{ fontSize: '11px', fontFamily: 'monospace', color: themeColors.text.secondary }}>
          {row.id.slice(0, 8)}…
        </Typography>
      ),
    },
    {
      id: 'leadName',
      label: 'Lead Name',
      render: (_: any, row: FormSubmissionResponse) => (
        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: themeColors.text.primary }}>
          {row.leadName || '—'}
        </Typography>
      ),
    },
    {
      id: 'leadEmail',
      label: 'Email',
      render: (_: any, row: FormSubmissionResponse) => (
        <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary }}>
          {row.leadEmail || '—'}
        </Typography>
      ),
    },
    {
      id: 'leadPhone',
      label: 'Phone',
      render: (_: any, row: FormSubmissionResponse) => (
        <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary }}>
          {maskPhoneNumber(row.leadPhone)}
        </Typography>
      ),
    },
    {
      id: 'formName',
      label: 'Form',
      render: (_: any, row: FormSubmissionResponse) => (
        <Chip
          label={row.formName}
          size="small"
          sx={{ fontSize: '11px', bgcolor: themeColors.infoLight, color: themeColors.primary, maxWidth: 160 }}
        />
      ),
    },
    {
      id: 'utmSource',
      label: 'UTM Source',
      render: (_: any, row: FormSubmissionResponse) =>
        row.utmSource ? (
          <Chip label={row.utmSource} size="small" sx={{ fontSize: '10px', bgcolor: themeColors.warningLight, color: themeColors.warning }} />
        ) : (
          <Typography sx={{ fontSize: '12px', color: themeColors.text.muted }}>—</Typography>
        ),
    },
    {
      id: 'createdAt',
      label: 'Submitted At',
      render: (_: any, row: FormSubmissionResponse) => (
        <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary }}>
          {new Date(row.createdAt).toLocaleString()}
        </Typography>
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      align: 'right' as const,
      render: (_: any, row: FormSubmissionResponse) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="View Response">
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); setSelectedResponse(row) }}
              sx={{ color: themeColors.primary }}
            >
              <ViewIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          {row.leadId && (
            <Tooltip title="Open Lead">
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); navigate(`/leads/${row.leadId}`) }}
                sx={{ color: themeColors.info }}
              >
                <OpenLeadIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ]

  if (isLoading) return <LoadingSpinner fullHeight label="Loading responses..." />

  return (
    <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography sx={{ fontSize: '13px', color: themeColors.text.secondary }}>
          {formData?.name ?? 'Loading…'} · {total} total submissions
        </Typography>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={() => refetch()} sx={{ color: themeColors.text.secondary }}>
            <RefreshIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Inline header shown when embedded */}
      {embedded && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography sx={{ fontSize: '13px', color: themeColors.text.secondary }}>
            {total} total submissions
          </Typography>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={() => refetch()} sx={{ color: themeColors.text.secondary }}>
              <RefreshIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      <DataTable
        columns={columns}
        rows={responses}
        onRowClick={(row) => setSelectedResponse(row)}
        emptyMessage="No submissions yet"
        emptyHint="Share the form link to start capturing leads"
        pagination={total > pageSize ? {
          page,
          pageSize,
          total,
          totalPages,
          onPageChange: setPage,
        } : undefined}
      />

      {/* Response detail dialog */}
      <Dialog
        open={!!selectedResponse}
        onClose={() => setSelectedResponse(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '16px', color: themeColors.text.primary, pb: 1 }}>
          Submission Details
          <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary, fontWeight: 400 }}>
            Submitted {selectedResponse ? new Date(selectedResponse.createdAt).toLocaleString() : ''}
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          {selectedResponse && (
            <Stack spacing={0}>
              {/* UTM info */}
              {(selectedResponse.utmSource || selectedResponse.utmCampaign) && (
                <Box sx={{ p: 1.5, bgcolor: themeColors.warningLight, borderRadius: '8px', mb: 2 }}>
                  <Typography sx={{ fontSize: '11px', fontWeight: 600, color: themeColors.warning, mb: 0.75 }}>
                    Marketing Source
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {selectedResponse.utmSource && <Chip label={`Source: ${selectedResponse.utmSource}`} size="small" sx={{ fontSize: '11px' }} />}
                    {selectedResponse.utmMedium && <Chip label={`Medium: ${selectedResponse.utmMedium}`} size="small" sx={{ fontSize: '11px' }} />}
                    {selectedResponse.utmCampaign && <Chip label={`Campaign: ${selectedResponse.utmCampaign}`} size="small" sx={{ fontSize: '11px' }} />}
                  </Box>
                </Box>
              )}

              {/* Form data */}
              <Table size="small">
                <TableBody>
                  {Object.entries(selectedResponse.submissionData || {}).map(([key, val]) => {
                    const isPhoneKey = /phone|mobile|contact/i.test(key)
                    const displayVal = Array.isArray(val)
                      ? val.join(', ')
                      : isPhoneKey && typeof val === 'string'
                      ? maskPhoneNumber(val)
                      : String(val ?? '—')
                    return (
                      <TableRow key={key} sx={{ '&:last-child td': { border: 0 } }}>
                        <TableCell sx={{ fontSize: '12px', color: themeColors.text.secondary, fontWeight: 600, width: '40%', py: 0.75, pl: 0 }}>
                          {key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        </TableCell>
                        <TableCell sx={{ fontSize: '13px', color: themeColors.text.primary, py: 0.75, pr: 0 }}>
                          {displayVal}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </Stack>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
          {selectedResponse?.leadId && (
            <Button
              size="small"
              startIcon={<OpenLeadIcon sx={{ fontSize: 14 }} />}
              onClick={() => { navigate(`/leads/${selectedResponse!.leadId}`); setSelectedResponse(null) }}
              sx={{ fontSize: '13px', color: themeColors.primary }}
            >
              Open Lead
            </Button>
          )}
          <Button size="small" onClick={() => setSelectedResponse(null)} sx={{ fontSize: '13px' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
